import React, { useEffect, useState } from 'react';
import useShapeStore from '../store/useShapeStore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

const { FiTrash2, FiRotateCw, FiCopy, FiRepeat, FiLock, FiUnlock } = FiIcons;

const ShapePropertiesPanel = () => {
  const { selectedShapeId, shapeItems, updateShapeItem, removeShapeItem, rotateShapeItem, duplicateShapeItem, flipShapeItem } = useShapeStore();
  const selectedShape = selectedShapeId ? shapeItems[selectedShapeId] : null;

  const [localState, setLocalState] = useState({ 
    rotation: 0, scale: 1, fill: '#FFFFFF', stroke: '#000000', strokeWidth: 2,
    width: 0, height: 0, deg: 0
  });

  useEffect(() => { 
    if (selectedShape) { 
      setLocalState({ 
        rotation: selectedShape.rotation, 
        scale: selectedShape.scale, 
        fill: selectedShape.customFill, 
        stroke: selectedShape.customStroke, 
        strokeWidth: selectedShape.customStrokeWidth || 1,
        width: Math.round(selectedShape.dimensions.width),
        height: Math.round(selectedShape.dimensions.height),
        deg: Math.round(selectedShape.rotation * 180 / Math.PI)
      }); 
    } 
  }, [selectedShape]);

  if (!selectedShape) return null;

  const handleDimensionChange = (key, val) => {
    const num = parseInt(val);
    if (isNaN(num) || num <= 0) return;
    updateShapeItem(selectedShapeId, { 
      dimensions: { ...selectedShape.dimensions, [key]: num } 
    });
  };

  const handleRotationChange = (deg) => {
    let val = parseInt(deg);
    if (isNaN(val)) val = 0;
    updateShapeItem(selectedShapeId, { rotation: val * Math.PI / 180 });
  };

  return (
    <div className="absolute left-20 top-20 w-64 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-10">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 text-center">
        <h3 className="font-semibold text-gray-700 text-sm">Edycja Kształtu</h3>
      </div>
      <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
        
        {/* Odbicie */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Orientacja</div>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => flipShapeItem(selectedShapeId, 'horizontal')}
              className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedShape.flipX ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
            >
              <SafeIcon icon={FiRepeat} className="text-lg" />
              <span className="text-[10px] font-medium uppercase">Odbij Poziom</span>
            </button>
            <button 
              onClick={() => flipShapeItem(selectedShapeId, 'vertical')}
              className={clsx("flex flex-col items-center gap-1 p-2 border rounded-lg transition-all", selectedShape.flipY ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "hover:bg-gray-50 border-gray-200 text-gray-600")}
            >
              <SafeIcon icon={FiRepeat} className="text-lg rotate-90" />
              <span className="text-[10px] font-medium uppercase">Odbij Pion</span>
            </button>
          </div>
        </div>

        {/* Wymiary */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Wymiary</div>
            <button 
              onClick={() => updateShapeItem(selectedShapeId, { lockAspectRatio: !selectedShape.lockAspectRatio })}
              className={clsx("p-1 rounded transition-colors", selectedShape.lockAspectRatio ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:bg-gray-100")}
            >
              <SafeIcon icon={selectedShape.lockAspectRatio ? FiLock : FiUnlock} className="text-sm" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 bg-gray-50 p-2 rounded-lg">
             <div className="space-y-1">
                <label className="text-[10px] text-gray-400 uppercase">Szerokość</label>
                <input type="number" value={localState.width} onChange={(e) => handleDimensionChange('width', e.target.value)} className="w-full h-8 border rounded px-2 text-sm" />
             </div>
             <div className="space-y-1">
                <label className="text-[10px] text-gray-400 uppercase">Wysokość</label>
                <input type="number" value={localState.height} onChange={(e) => handleDimensionChange('height', e.target.value)} className="w-full h-8 border rounded px-2 text-sm" />
             </div>
          </div>
        </div>

        {/* Obrót */}
        <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Obrót (°)</label>
              <div className="flex gap-1">
                <button onClick={() => rotateShapeItem(selectedShapeId, -Math.PI/4)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200">-45°</button>
                <button onClick={() => rotateShapeItem(selectedShapeId, Math.PI/4)} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200">+45°</button>
              </div>
            </div>
            <div className="flex items-center gap-2">
                <input type="number" value={localState.deg} onChange={(e) => handleRotationChange(e.target.value)} className="h-8 w-16 rounded border border-gray-300 px-2 text-sm text-center" />
                <input type="range" min="0" max="360" value={localState.deg % 360} onChange={(e) => handleRotationChange(e.target.value)} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
            </div>
        </div>

        {/* Wygląd */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Wygląd</div>
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600">Grubość obrysu:</label>
              <div className="flex items-center gap-2">
                <input type="range" min="0.1" max="10" step="0.1" value={localState.strokeWidth} onChange={(e) => updateShapeItem(selectedShapeId, { customStrokeWidth: parseFloat(e.target.value) })} className="w-20 h-2 bg-gray-200 rounded-lg accent-indigo-600" />
                <span className="text-xs font-mono w-6 text-right">{localState.strokeWidth}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-1">
                <div className="flex flex-col items-center gap-1">
                  <label className="text-[10px] text-gray-500 uppercase">Wypełnienie</label>
                  <input type="color" value={localState.fill} onChange={(e) => updateShapeItem(selectedShapeId, { customFill: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <label className="text-[10px] text-gray-500 uppercase">Obrys</label>
                  <input type="color" value={localState.stroke} onChange={(e) => updateShapeItem(selectedShapeId, { customStroke: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                </div>
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 space-y-2">
            <button onClick={() => duplicateShapeItem(selectedShapeId)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"><SafeIcon icon={FiCopy} /> Duplikuj</button>
            <button onClick={() => removeShapeItem(selectedShapeId)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"><SafeIcon icon={FiTrash2} /> Usuń</button>
        </div>
      </div>
    </div>
  );
};

export default ShapePropertiesPanel;